// Teams PR A: the pure pieces of a person's own Microsoft Teams sign-in (delegated OAuth on the organization's EXISTING mailbox
// connector app registration). No network, no secrets: shared by the browser screen and the teams-connection Edge Function
// (supabase/functions/teams-connection imports this file, the same way team-invite imports src/lib/authMailDoor.js).

// Every delegated Microsoft Graph permission PR A + PR B ask for at sign-in, so a person consents once.
// Val adds these to the app registration and grants admin consent (Docs/Open_Questions_Teams_Integration_Build.md).
export const TEAMS_DELEGATED_SCOPES = Object.freeze([
    'openid',
    'profile',
    'offline_access',
    'User.Read',
    'User.ReadBasic.All',
    'Chat.Read',
    'Chat.Create',
    'ChatMessage.Send',
    'ChannelMessage.Read.All',
    'ChannelMessage.Send',
    'Team.ReadBasic.All',
    'Channel.ReadBasic.All',
    'ChannelMember.Read.All',
]);

export const TEAMS_CALLBACK_PATH = '/settings/communications/teams-mirror';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isTenantId(value) {
    return typeof value === 'string' && GUID.test(value.trim());
}

/** The Microsoft sign-in address. Single-tenant: the linked organization's own endpoint, so other tenants cannot even sign in. */
export function buildAuthorizeUrl({ tenantId, clientId, redirectUri, state, scopes = TEAMS_DELEGATED_SCOPES }) {
    if (!isTenantId(tenantId)) throw new Error('The linked Microsoft 365 organization id is not valid.');
    if (!clientId) throw new Error('The Microsoft app registration is missing.');
    if (!redirectUri || !/^https:\/\//.test(redirectUri)) throw new Error('The Teams return address must be https.');
    if (!state || String(state).length < 32) throw new Error('The sign-in state is missing.');
    const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        redirect_uri: redirectUri,
        response_mode: 'query',
        scope: scopes.join(' '),
        state,
        prompt: 'select_account',
    });
    return `https://login.microsoftonline.com/${tenantId.trim().toLowerCase()}/oauth2/v2.0/authorize?${params.toString()}`;
}

export function tokenEndpoint(tenantId) {
    if (!isTenantId(tenantId)) throw new Error('The linked Microsoft 365 organization id is not valid.');
    return `https://login.microsoftonline.com/${tenantId.trim().toLowerCase()}/oauth2/v2.0/token`;
}

/** The form body for exchanging the one-time code. */
export function tokenRequestBody({ clientId, clientSecret, code, redirectUri, scopes = TEAMS_DELEGATED_SCOPES }) {
    return new URLSearchParams({
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        scope: scopes.join(' '),
    });
}

function base64UrlDecode(segment) {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
    const binary = globalThis.atob(padded);
    // UTF-8 decode without TextDecoder (missing in some test DOMs); Deno and browsers give the same result either way.
    return decodeURIComponent(Array.from(binary, (character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
}

/** Read a token's claims. Only for tokens received directly from Microsoft's token endpoint over TLS (never from the browser). */
export function decodeJwtPayload(token) {
    if (typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        return JSON.parse(base64UrlDecode(parts[1]));
    } catch {
        return null;
    }
}

/** Who signed in, from the token response. tid is the Microsoft tenant the account belongs to. */
export function identityFromTokenResponse(response) {
    const id = decodeJwtPayload(response?.id_token) || {};
    const access = decodeJwtPayload(response?.access_token) || {};
    const tenantId = String(id.tid || access.tid || '').toLowerCase();
    const scopeText = typeof response?.scope === 'string' ? response.scope : (access.scp || '');
    return {
        tenantId,
        userId: id.oid || access.oid || null,
        upn: id.preferred_username || access.upn || access.unique_name || null,
        name: id.name || access.name || null,
        scopes: scopeText.split(' ').map((item) => item.trim()).filter(Boolean),
    };
}

/** Staff may connect only accounts from the linked Microsoft 365 organization. */
export function tenantMatches(linkedTenantId, accountTenantId) {
    return isTenantId(linkedTenantId) && isTenantId(accountTenantId)
        && linkedTenantId.trim().toLowerCase() === accountTenantId.trim().toLowerCase();
}

/** Microsoft sends the person back with ?code=...&state=... (or ?error=...). */
export function parseCallbackParams(search) {
    const params = new URLSearchParams(search || '');
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    if (!code && !error) return null;
    return { code, state, error, errorDescription: params.get('error_description') };
}
